import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { GraduationCap, Target, Sparkles } from 'lucide-react';

const About = ({ education }) => {
  if (!education) return null;

  return (
    <section id="about" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">About Me</h2>
          <div className="w-20 h-1 bg-blue-900 mx-auto"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Academic Journey */}
          <Card className="border-2 border-gray-100 hover:border-blue-200 transition-all transform hover:scale-105 hover:shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <GraduationCap className="w-6 h-6 text-blue-900" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Academic Journey</h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-lg text-gray-900">{education.degree}</h4>
                  <p className="text-blue-900 font-medium">{education.institution}</p>
                  <p className="text-gray-600 text-sm">{education.location}</p>
                  <p className="text-gray-600 text-sm">{education.duration}</p>
                  <p className="text-gray-900 font-semibold mt-2">CGPA: {education.cgpa}</p>
                </div>

                <div className="pt-4">
                  <h5 className="font-semibold text-gray-900 mb-3">Relevant Coursework</h5>
                  <div className="flex flex-wrap gap-2">
                    {education.relevantCoursework.map((course, index) => (
                      <Badge key={index} variant="secondary" className="bg-slate-100 text-gray-700">
                        {course}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Research Philosophy */}
          <Card className="border-2 border-gray-100 hover:border-blue-200 transition-all transform hover:scale-105 hover:shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <Sparkles className="w-6 h-6 text-emerald-700" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Research Philosophy</h3>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  My research journey began with curiosity in Python and foundational machine learning, 
                  evolving into a focused academic direction through hands-on research, publications, 
                  and manuscript-level work.
                </p>
                <p>
                  I believe that in safety-critical AI systems, <strong>interpretability, robustness, 
                  and temporal reasoning</strong> are as important as predictive accuracy. This philosophy 
                  drives my work in neuro-symbolic systems and efficient AI architectures.
                </p>
                <p>
                  My approach combines <strong>structured reasoning with data-driven learning</strong>, 
                  ensuring that AI systems are not just accurate but also reliable, explainable, 
                  and deployable in real-world scenarios.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Goals Section - Optimized */}
        <Card className="mt-12 border-2 border-gray-100 hover:border-blue-200 transition-all transform hover:scale-105 hover:shadow-xl">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Target className="w-6 h-6 text-blue-900" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Research Goals & Vision</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold text-lg text-blue-900 mb-3">Current Focus</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-900 mt-1">•</span>
                    <span>Advancing research in efficient Vision Transformers and model compression</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-900 mt-1">•</span>
                    <span>Developing neuro-symbolic systems for safety-critical applications</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-900 mt-1">•</span>
                    <span>Strengthening theoretical foundations in optimization and probabilistic modeling</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-900 mt-1">•</span>
                    <span>Contributing to cutting-edge research in computer vision and machine learning</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold text-lg text-blue-900 mb-3">Long-Term Vision</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-900 mt-1">•</span>
                    <span>Bridge theoretical innovation with real-world AI deployment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-900 mt-1">•</span>
                    <span>Develop expertise in high-performance, safety-critical AI systems</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-900 mt-1">•</span>
                    <span>Contribute to applied AI research in industrial and autonomous domains</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-900 mt-1">•</span>
                    <span>Advance Green AI and sustainable machine learning practices</span>
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default About;
